import { useAskRedoSlidePanel } from "@redotech/redo-merchant-app-common/ask-redo/ask-redo-slide-panel-context";
import { LegacyAiPanelBridge } from "@redotech/redo-merchant-app-common/ask-redo/legacy-ai-panel-bridge";
import { ClientUploader } from "@redotech/redo-merchant-app-common/client/upload";
import { AiSlidePanelLayout } from "@redotech/redo-web/ai/chat/components/ai-slide-panel-layout";
import { lazy, memo, ReactNode, Suspense } from "react";
import { AskRedoEscalationWatcher } from "../escalation/ask-redo-escalation-watcher";
import { EscalationAuthorProvider } from "../escalation/escalation-author-context";

/**
 * Wraps the merchant app shell with the Ask Redo slide-out column.
 *
 * Two responsibilities:
 *
 *   1. **Always-on Ask Redo shell.** The layout owns the slide-out
 *      grid, escalation watcher, legacy open bridge, and uploader
 *      provider for the merchant app.
 *
 *   2. **Bundle gate (panel-only).** The heavy bit is the slide-out
 *      content (it transitively imports the AI SDK, transport, and
 *      every chat component). We `React.lazy()` that — but ONLY the
 *      panel slot, not the grid around the merchant content. Wrapping
 *      `children` inside a Suspense boundary would mean every route's
 *      state resets when the chunk lands, because React treats
 *      fallback children and resolved children as separate subtrees.
 *      Wrapping just the panel keeps `children` mounted at the same
 *      identity through the load.
 *
 * The grid + resize / notification-offset / focus-on-open behaviour
 * comes from the shared `AiSlidePanelLayout` primitive (also used by
 * onboarding's `GlobalAskRedo`). We drive its `isOpen` from our own
 * context so the panel's state is isolated from legacy open requests.
 */
const LazyAskRedoSlideOut = lazy(() =>
  import("../slide-out/ask-redo-slide-out").then((m) => ({
    default: m.AskRedoSlideOut,
  })),
);

export const AskRedoLayout = memo(function AskRedoLayout({
  children,
}: {
  children: ReactNode;
}) {
  const { isOpen } = useAskRedoSlidePanel();

  // `ClientUploader` provides `UploaderContext` so the prompt
  // input can offer image/file uploads.
  // `EscalationAuthorProvider` wraps BOTH the watcher (which writes
  // agent name/avatar as SSE events arrive) and the slide-out
  // (which reads them when rendering the banner + human message
  // bubbles). Mounting it here means the agent identity persists
  // across slide-out close/reopen cycles — the slide-out unmounts
  // freely, but the captured author info survives because the
  // provider lives at the layout level.
  return (
    <ClientUploader>
      <EscalationAuthorProvider>
        <AskRedoEscalationWatcher />
        {/* Redirects un-migrated legacy `useAiSlidePanel().open()`
            call sites into the Ask Redo slide-out — without it they
            open an empty legacy column (OMS-3245's "black box"). */}
        <LegacyAiPanelBridge />
        <AiSlidePanelLayout
          isOpen={isOpen}
          panel={
            // Suspense renders nothing while the chunk loads — the
            // panel's column collapses to 0 width when `isOpen` is
            // false anyway, so there's nothing to show.
            <Suspense fallback={null}>
              <LazyAskRedoSlideOut />
            </Suspense>
          }
        >
          {children}
        </AiSlidePanelLayout>
      </EscalationAuthorProvider>
    </ClientUploader>
  );
});
